import React from "react";
import { View, Pressable, StyleSheet } from "react-native";
import {
  C,
  RText,
  Tag,
  Check,
  AddressLine,
  tpName,
} from "@components/ds/theme";
import { t } from "@constants/localization";
import { BUCKET, getTpChip, getTpCta, shortDate } from "../helpers";

const PATH = "Receiving/bucket";

// Status pill colours (mockup §9 frames 02–04): TO BE RACKED amber,
// "1 OF 2 RACKS" grey, RACK B-04 green.
const CHIP_LOOK = {
  toRack: { bg: C.pendingBg, color: C.pending },
  inProgress: { bg: C.track, color: C.muted },
  racked: { bg: C.greenBg, color: C.green },
  disabled: { bg: C.track, color: C.muted },
};

// EDD pill: blue on Today, red on Pending (overdue), amber on Advance until
// it is racked, then blue (frame 03's racked TP).
const eddLook = (bucket, racked) => {
  if (bucket === BUCKET.PENDING) return { bg: C.redBg, color: C.red };
  if (bucket === BUCKET.ADVANCE && !racked) {
    return { bg: C.pendingBg, color: C.pending };
  }
  return { bg: C.blueBg, color: C.blueLine };
};

const eddText = (tp, bucket) =>
  bucket === BUCKET.TODAY
    ? t(`${PATH}/eddToday`)
    : t(`${PATH}/edd`, { date: shortDate(tp.date) });

const Cta = ({ cta, advance, testID }) => {
  if (cta.action === "RACK" && !advance) {
    return (
      <View testID={testID} style={[styles.cta, styles.ctaFilled]}>
        <RText size={13} weight={600} color={C.surface}>
          {t(`${PATH}/rackCta`)}
        </RText>
      </View>
    );
  }
  if (cta.action === "RACK") {
    return (
      <View testID={testID} style={[styles.cta, styles.ctaAdvance]}>
        <RText size={13} weight={600} color={C.green}>
          {t(`${PATH}/advanceCta`)}
        </RText>
      </View>
    );
  }
  return (
    <View testID={testID} style={[styles.cta, styles.ctaOutline]}>
      <RText size={13} weight={600}>
        {t(`${PATH}/continueCta`)}
      </RText>
    </View>
  );
};

/**
 * One TP on a racking list, pixel-matched to the mockup (§9 frames 02–04):
 * customer name, address, TP id · shipments · locality, EDD + status pills; on the right Rack
 * (green), Continue (outline), Advance rack (green outline) or, once racked,
 * a faded card with a green tick. Press rules are the screen's (getTpCta).
 */
const RackTpRow = ({ item, bucket, onPress = () => {} }) => {
  const chip = getTpChip(item);
  const cta = getTpCta(item);
  const done = cta.action === "DONE";
  const disabled = cta.action === "DISABLED";
  const chipLook = CHIP_LOOK[chip.tone] || CHIP_LOOK.toRack;
  const edd = eddLook(bucket, done);
  const count = Number(item.shipment_count ?? 0);
  const meta = [
    t(`${PATH}/${count === 1 ? "shipment" : "shipments"}`, {
      count: String(count),
    }),
    item.area,
  ]
    .filter(Boolean)
    .join(" · ");
  const title = tpName(item);
  const chipLabel = chip.label ?? t(`${PATH}/labelsMissingChip`);

  let action;
  if (done) {
    action = (
      <View testID={`receiving-tp-row-done-${item.cx_id}`} style={styles.tick}>
        <Check size={15} />
      </View>
    );
  } else if (disabled) {
    action = (
      <RText size={11} weight={500} color={C.muted} style={styles.disabled}>
        {t(`${PATH}/labelsMissing`)}
      </RText>
    );
  } else {
    action = (
      <Cta
        cta={cta}
        advance={Boolean(item.advance)}
        testID={`receiving-tp-row-cta-${item.cx_id}`}
      />
    );
  }

  return (
    <Pressable
      testID={`receiving-tp-row-${item.cx_id}`}
      disabled={disabled}
      onPress={() => onPress(item)}
      style={[styles.card, done && styles.done]}
    >
      <View style={styles.info}>
        <RText size={17} weight={700} style={styles.tight}>
          {title}
        </RText>
        <AddressLine address={item.address} />
        {meta ? (
          <RText size={12} weight={500} color={C.muted}>
            {meta}
          </RText>
        ) : null}
        <View style={styles.tags}>
          <Tag label={eddText(item, bucket)} bg={edd.bg} color={edd.color} />
          <Tag label={chipLabel} bg={chipLook.bg} color={chipLook.color} />
        </View>
      </View>
      {action}
    </Pressable>
  );
};

export default RackTpRow;

const styles = StyleSheet.create({
  card: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    backgroundColor: C.surface,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 12,
    padding: 14,
  },
  done: { opacity: 0.6 },
  info: { flex: 1, gap: 6 },
  tight: { letterSpacing: -0.2 },
  tags: { flexDirection: "row", flexWrap: "wrap", gap: 6 },
  cta: { borderRadius: 8, flexShrink: 0 },
  ctaFilled: {
    backgroundColor: C.green,
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  ctaOutline: {
    backgroundColor: C.surface,
    borderWidth: 1.5,
    borderColor: C.border,
    paddingVertical: 9,
    paddingHorizontal: 16,
  },
  ctaAdvance: {
    backgroundColor: C.surface,
    borderWidth: 1.5,
    borderColor: C.green,
    paddingVertical: 9,
    paddingHorizontal: 14,
  },
  tick: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: C.green,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  disabled: { maxWidth: 90, textAlign: "right" },
});
